import { useState } from 'react';
import { cn } from '@/lib/utils';
import { calendarMonthGrid, formatEtDate, monthLabel, shiftMonth, WEEKDAYS_SHORT } from '@/lib/cashFlow';

/**
 * A visual from–to range picker (a real month calendar) shared by BOTH primary-view
 * lenses through `PeriodPicker` (replit.md L14: one window, every lens). It is
 * presentational + controlled: it owns only the visible month and the two draft
 * endpoints, and reports the applied range upward; the caller writes it to the SHARED
 * workspace window. The first click sets the start, the second the end (clicking an
 * earlier day swaps them); a third click after a complete range starts over. It holds
 * NO date math of its own — the grid and the month stepping come from the pure lib
 * helpers (`calendarMonthGrid`, `shiftMonth`), so date logic lives in one place (L9).
 */
export interface CalendarRangePickerProps {
  /** The year the calendar opens on (the shared workspace year). */
  year: number;
  /** The month (1–12) the calendar opens on. */
  month: number;
  /** Reports the chosen `[from, to]` ISO range when the user applies it. */
  onApply: (from: string, to: string) => void;
}

export function CalendarRangePicker({ year, month, onApply }: CalendarRangePickerProps) {
  const [view, setView] = useState({ year, month });
  const [from, setFrom] = useState<string | null>(null);
  const [to, setTo] = useState<string | null>(null);

  const grid = calendarMonthGrid(view.year, view.month);
  const ready = from !== null && to !== null;

  const pick = (iso: string) => {
    if (from === null || to !== null) {
      // First click, or a click after a complete range → start a fresh selection.
      setFrom(iso);
      setTo(null);
      return;
    }
    // Second click closes the range; an earlier day swaps the two endpoints.
    if (iso < from) {
      setTo(from);
      setFrom(iso);
    } else {
      setTo(iso);
    }
  };

  const inRange = (iso: string): boolean => from !== null && to !== null && iso >= from && iso <= to;
  const isEndpoint = (iso: string): boolean => iso === from || iso === to;

  return (
    <div className="mt-3 border-t border-border pt-2">
      <div className="mb-1.5 flex items-center justify-between">
        <button
          type="button"
          aria-label="Eelmine kuu"
          onClick={() => setView((v) => shiftMonth(v.year, v.month, -1))}
          className="rounded px-1.5 py-0.5 text-sm leading-none text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          ‹
        </button>
        <span className="text-xs font-medium text-foreground">{monthLabel(view.year, view.month)}</span>
        <button
          type="button"
          aria-label="Järgmine kuu"
          onClick={() => setView((v) => shiftMonth(v.year, v.month, 1))}
          className="rounded px-1.5 py-0.5 text-sm leading-none text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          ›
        </button>
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-center">
        {WEEKDAYS_SHORT.map((w) => (
          <span key={w} className="py-0.5 text-[9px] font-medium uppercase text-muted-foreground">
            {w}
          </span>
        ))}
        {grid.map((cell) => (
          <button
            key={cell.iso}
            type="button"
            onClick={() => pick(cell.iso)}
            aria-pressed={isEndpoint(cell.iso)}
            className={cn(
              'rounded py-1 text-[11px] tabular-nums transition-colors',
              cell.inMonth ? 'text-foreground' : 'text-muted-foreground/40',
              isEndpoint(cell.iso)
                ? 'bg-primary text-primary-foreground'
                : inRange(cell.iso)
                  ? 'bg-primary/15 text-primary'
                  : 'hover:bg-accent',
            )}
          >
            {cell.day}
          </button>
        ))}
      </div>
      <div className="mt-2 flex items-center justify-between text-[10px] text-muted-foreground">
        <span>Algus: {from ? formatEtDate(from) : '—'}</span>
        <span>Lõpp: {to ? formatEtDate(to) : '—'}</span>
      </div>
      <button
        type="button"
        disabled={!ready}
        onClick={() => onApply(from!, to!)}
        className="mt-2 w-full rounded-md bg-primary px-3 py-1 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-40"
      >
        Rakenda vahemik
      </button>
    </div>
  );
}
